import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { Search, Smartphone, CreditCard, Gift, Receipt, Calendar, Filter, Wallet } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppContext } from '../../../context/AppContext';
import { AdminGetAllTransaction } from '../../../utils/api';

const getTypeIcon = (type) => {
  switch (type) {
    case 'upi':
      return <Smartphone className="h-5 w-5" color="#3182CE" />; // blue.500
    case 'crypto':
      return <CreditCard className="h-5 w-5" color="#DD6B20" />; // orange.500
    case 'voucher':
      return <Gift className="h-5 w-5" color="#38A169" />; // green.500
    default:
      return <Receipt className="h-5 w-5" color="#718096" />; // gray.500
  }
};

const TransactionHistory = () => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUser, setSearchUser] = useState(searchParams.get('userId') || '');
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [filterType, setFilterType] = useState('');
  const [otpcartBalance, setOtpcartBalance] = useState(0);
  const { AdminToken } = useContext(AppContext);

  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await AdminGetAllTransaction(AdminToken, currentPage, itemsPerPage, searchTerm, searchUser, dateFrom, dateTo, filterType);

        setTransactions(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setOtpcartBalance(res.data.totalBalance || 0);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    };

    fetchTransactions();
  }, [currentPage, itemsPerPage, searchTerm, searchUser, dateFrom, dateTo, filterType]);

  const clearDateFilters = () => {
    setDateFrom(new Date().toISOString().split('T')[0]);
    setDateTo(new Date().toISOString().split('T')[0]);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchUserChange = (e) => {
    setSearchUser(e.target.value);
    setCurrentPage(1);
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, transactions.length + (currentPage - 1) * itemsPerPage);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Theme Colors
  const bg = "gray.900";
  const textColor = "white";
  const subTextColor = "gray.400";
  const borderColor = "gray.700";
  const headerBg = "gray.800";
  const themeColor = "#319795";

  return (
    <Box p={6} bg={bg} minH="100vh" w="100%">
      <VStack spacing={6} align="stretch">
        <Flex direction="column" gap={4}>
          <Heading size="lg" color={textColor}>Transaction Management</Heading>

          <Box bg={headerBg} p={4} rounded="lg" shadow="sm" borderWidth="1px" borderColor={borderColor} w="fit-content" minW="300px">
            <Flex align="center" gap={4}>
              <Flex p={3} bg="teal.900" rounded="full" align="center" justify="center">
                <Wallet className="h-6 w-6" color={themeColor} />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={subTextColor}>Techyindia Mobile Balance</Text>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  ₹{otpcartBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </Box>
            </Flex>
          </Box>
        </Flex>

        {/* Search and Filters */}
        <Box bg={headerBg} p={6} rounded="lg" shadow="sm" borderWidth="1px" borderColor={borderColor}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none">
                <Search className="h-5 w-5" color={subTextColor} />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Search UTR"
                value={searchTerm}
                onChange={handleSearchChange}
                color={textColor}
                borderColor={borderColor}
                _placeholder={{ color: subTextColor }}
              />
            </InputGroup>

            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none">
                <Search className="h-5 w-5" color={subTextColor} />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Search Email or User ID"
                value={searchUser}
                onChange={handleSearchUserChange}
                color={textColor}
                borderColor={borderColor}
                _placeholder={{ color: subTextColor }}
              />
            </InputGroup>

            <Flex align="center" gap={2} minW="200px">
              <Filter className="h-5 w-5" color={subTextColor} />
              <Select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(1);
                }}
                color={textColor}
                bg={headerBg}
                borderColor={borderColor}
              >
                <option value="" style={{ background: headerBg }}>All Types</option>
                <option value="upi" style={{ background: headerBg }}>UPI</option>
                <option value="crypto" style={{ background: headerBg }}>Crypto</option>
                <option value="voucher" style={{ background: headerBg }}>Voucher</option>
              </Select>
            </Flex>
          </Flex>

          {/* Date Range Filter */}
          <Box mt={4} pt={4} borderTopWidth="1px" borderColor={borderColor}>
            <Flex direction="column" gap={4}>
              <Flex align="center" gap={2}>
                <Calendar className="h-4 w-4" color={subTextColor} />
                <Text fontSize="sm" fontWeight="medium" color={textColor}>Date Range:</Text>
              </Flex>

              <Flex direction={{ base: 'column', sm: 'row' }} gap={{ base: 3, sm: 4 }}>
                <Flex align="center" gap={2} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" color={subTextColor} minW={{ sm: '50px' }}>From:</Text>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    size="sm"
                    rounded="md"
                    color={textColor}
                    borderColor={borderColor}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)'
                      }
                    }}
                  />
                </Flex>

                <Flex align="center" gap={2} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" color={subTextColor} minW={{ sm: '30px' }}>To:</Text>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    size="sm"
                    rounded="md"
                    color={textColor}
                    borderColor={borderColor}
                    sx={{
                      '::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)'
                      }
                    }}
                  />
                </Flex>
              </Flex>

              <Flex direction={{ base: 'column', sm: 'row' }} gap={3} align={{ sm: 'center' }} justify={{ sm: 'space-between' }}>
                {(dateFrom || dateTo) && (
                  <Button
                    onClick={clearDateFilters}
                    size="sm"
                    variant="outline"
                    color={textColor}
                    borderColor={borderColor}
                    _hover={{ bg: "gray.700" }}
                  >
                    Clear Dates
                  </Button>
                )}

                {(dateFrom || dateTo) && (
                  <Box fontSize="sm" color={subTextColor} bg={bg} px={3} py={2} rounded="md" borderWidth="1px" borderColor={borderColor}>
                    {dateFrom && dateTo && (
                      <Text fontWeight="medium">
                        📅 {new Date(dateFrom).toLocaleDateString()} → {new Date(dateTo).toLocaleDateString()}
                      </Text>
                    )}
                    {dateFrom && !dateTo && (
                      <Text fontWeight="medium">
                        📅 From {new Date(dateFrom).toLocaleDateString()} onwards
                      </Text>
                    )}
                    {!dateFrom && dateTo && (
                      <Text fontWeight="medium">
                        📅 Up to {new Date(dateTo).toLocaleDateString()}
                      </Text>
                    )}
                  </Box>
                )}
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/* Transaction Table */}
        <Box bg={headerBg} rounded="lg" shadow="sm" borderWidth="1px" borderColor={borderColor} overflowX="auto">
          <TableContainer>
            <Table variant="simple">
              <Thead bg={bg}>
                <Tr>
                  <Th color={subTextColor} borderColor={borderColor}>Email</Th>
                  <Th color={subTextColor} borderColor={borderColor}>Amount</Th>
                  <Th color={subTextColor} borderColor={borderColor}>Transaction ID</Th>
                  <Th color={subTextColor} borderColor={borderColor}>Type</Th>
                  <Th color={subTextColor} borderColor={borderColor}>Time</Th>
                </Tr>
              </Thead>
              <Tbody>
                {transactions.map((transaction) => (
                  <Tr key={transaction._id} _hover={{ bg: bg }}>
                    <Td fontSize="sm" color={subTextColor} borderColor={borderColor}>{transaction?.user?.email}</Td>
                    <Td fontSize="sm" fontWeight="medium" color={textColor} borderColor={borderColor}>{transaction?.price?.toFixed(2)}</Td>
                    <Td fontSize="sm" fontWeight="medium" color={textColor} borderColor={borderColor}>{transaction?.transactionId}</Td>
                    <Td borderColor={borderColor}>
                      <Flex align="center">
                        {getTypeIcon(transaction.type)}
                        <Text ml={2} fontSize="sm" fontWeight="medium" color={textColor} textTransform="capitalize">
                          {transaction.type}
                        </Text>
                      </Flex>
                    </Td>
                    <Td fontSize="sm" fontWeight="medium" color={textColor} borderColor={borderColor}>{transaction.time}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {totalPages > 0 && (
            <Box px={6} py={4} borderTopWidth="1px" borderColor={borderColor} bg={headerBg}>
              <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" align="center" gap={4}>
                <Text fontSize="sm" color={subTextColor}>
                  Showing <Text as="span" fontWeight="medium" color={textColor}>{startItem}</Text> to{" "}
                  <Text as="span" fontWeight="medium" color={textColor}>{endItem}</Text> of{" "}
                  <Text as="span" fontWeight="medium" color={textColor}>{transactions.length}</Text> results
                </Text>

                <Flex align="center" gap={2}>
                  <Text fontSize="sm" color={subTextColor}>Show:</Text>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    size="sm"
                    w="auto"
                    color={textColor}
                    bg={headerBg}
                    borderColor={borderColor}
                  >
                    <option value={10} style={{ background: headerBg }}>10</option>
                    <option value={20} style={{ background: headerBg }}>20</option>
                    <option value={40} style={{ background: headerBg }}>40</option>
                    <option value={60} style={{ background: headerBg }}>60</option>
                    <option value={80} style={{ background: headerBg }}>80</option>
                    <option value={100} style={{ background: headerBg }}>100</option>
                  </Select>
                </Flex>

                <HStack spacing={1}>
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          size="sm"
                          variant="outline"
                          color={textColor}
                          borderColor={borderColor}
                          _hover={{ bg: "gray.700" }}
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(<Text key="ellipsis1" px={2} color={subTextColor}>...</Text>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          size="sm"
                          variant={i === currentPage ? 'solid' : 'outline'}
                          bg={i === currentPage ? themeColor : "transparent"}
                          color={i === currentPage ? "white" : textColor}
                          borderColor={i === currentPage ? themeColor : borderColor}
                          _hover={{ bg: i === currentPage ? themeColor : "gray.700" }}
                        >
                          {i}
                        </Button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<Text key="ellipsis2" px={2} color={subTextColor}>...</Text>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          size="sm"
                          variant="outline"
                          color={textColor}
                          borderColor={borderColor}
                          _hover={{ bg: "gray.700" }}
                        >
                          {totalPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </HStack>
              </Flex>
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default TransactionHistory;
